import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { TRACKER_SKIP_REASON_OUT_OF_SCOPE } from '@terra/shared-contracts-tracker';
import { type HttpRequest } from '../store/proxy-store';
import { useRequestBodies } from './useRequestBodies';
import type { useTrackRequest } from './useTrackRequest';
import {
  extractStatusCode,
  type TrackableRequest,
  type TrackableResponse,
} from './useTrackRequest';
import { useManualTrackWithCredentials } from './useManualTrackWithCredentials';

type BulkTrackEntry = {
  request: TrackableRequest;
  response: TrackableResponse;
};

type BulkTrackResult = {
  trackedCount: number;
  failedCount: number;
  skippedCount?: number;
  skipReason?: typeof TRACKER_SKIP_REASON_OUT_OF_SCOPE | undefined;
};

type BuildBulkTrackEntryParams = {
  request: HttpRequest;
  fetchBodies: ReturnType<typeof useRequestBodies>['fetchBodies'];
  resolveRequestBodyForTransport: ReturnType<
    typeof useRequestBodies
  >['resolveRequestBodyForTransport'];
};

type ShowBulkTrackResultToastParams = {
  result: BulkTrackResult;
  selectedCount: number;
  missingBodyCount: number;
  t: ReturnType<typeof useTranslations>;
};

async function buildBulkTrackEntry({
  request,
  fetchBodies,
  resolveRequestBodyForTransport,
}: BuildBulkTrackEntryParams): Promise<BulkTrackEntry | null> {
  const resolvedBody = await resolveRequestBodyForTransport({
    request,
    fallbackToBodyDb: true,
  });
  if (!resolvedBody) {
    return null;
  }

  const bodies = await fetchBodies(request.id);
  const responseBody = bodies?.responseBody || '';
  const statusCode = extractStatusCode(request);
  const timestamp =
    typeof request.timestamp === 'number'
      ? request.timestamp
      : typeof request.timestamp === 'string'
        ? new Date(request.timestamp).getTime()
        : request.timestamp.getTime();

  return {
    request: {
      method: request.method,
      url: request.url,
      headers: request.headers || {},
      body: resolvedBody.body,
      bodyEncoding: resolvedBody.bodyEncoding,
      httpVersion: request.httpVersion,
      timestamp,
    },
    response: {
      statusCode,
      statusMessage: request.responseStatusMessage || '',
      headers: request.responseHeaders || {},
      body: responseBody,
      duration: undefined,
      httpVersion: request.responseHttpVersion,
      timestamp,
    },
  };
}

function showBulkTrackResultToast({
  result,
  selectedCount,
  missingBodyCount,
  t,
}: ShowBulkTrackResultToastParams) {
  const trackedCount = result.trackedCount;
  const skippedCount = result.skippedCount ?? 0;
  const failedCount = result.failedCount + missingBodyCount;

  if (
    trackedCount === 0 &&
    failedCount === 0 &&
    skippedCount > 0 &&
    result.skipReason === TRACKER_SKIP_REASON_OUT_OF_SCOPE
  ) {
    toasterApi.warning({
      title: t('trackingOutOfScopeTitle'),
      description: t('trackingOutOfScopeDescription'),
    });
    return;
  }

  if (
    trackedCount === selectedCount &&
    failedCount === 0 &&
    skippedCount === 0
  ) {
    toasterApi.success({
      title: t('trackingSuccessTitle'),
      description: t('trackingSuccessDescription', {
        count: trackedCount,
      }),
    });
    return;
  }

  if (trackedCount > 0 || skippedCount > 0) {
    toasterApi.warning({
      title: t('trackingPartialTitle'),
      description: t('trackingPartialDescription', {
        succeeded: trackedCount,
        failed: failedCount,
        skipped: skippedCount,
      }),
    });
    return;
  }

  toasterApi.error({
    title: t('trackingErrorTitle'),
    description: t('trackingErrorDescription'),
  });
}

type UseBulkTrackRequestsParams = Pick<
  ReturnType<typeof useTrackRequest>,
  'trackRequests' | 'isTracking'
>;

export function useBulkTrackRequests({
  trackRequests,
  isTracking,
}: UseBulkTrackRequestsParams) {
  const t = useTranslations('httpHistory.bulkActions');
  const { fetchBodies, resolveRequestBodyForTransport } = useRequestBodies();
  const { requestManualTrack, isAwaitingManualTrackCredentials } =
    useManualTrackWithCredentials();
  const [isPreparingBulkTrack, setIsPreparingBulkTrack] = useState(false);

  const trackBulkRequests = useCallback(
    async ({ requests }: { requests: HttpRequest[] }) => {
      await requestManualTrack(
        async ({ credentialId }) => {
          setIsPreparingBulkTrack(true);
          try {
            const trackEntries = await Promise.all(
              requests.map((request) =>
                buildBulkTrackEntry({
                  request,
                  fetchBodies,
                  resolveRequestBodyForTransport,
                }),
              ),
            );
            const validTrackEntries = trackEntries.filter(
              (entry): entry is BulkTrackEntry => entry !== null,
            );
            const missingBodyCount = requests.length - validTrackEntries.length;
            const result =
              validTrackEntries.length > 0
                ? await trackRequests({
                    entries: validTrackEntries,
                    credentialId,
                    hideToast: true,
                  })
                : {
                    success: false,
                    trackedCount: 0,
                    failedCount: 0,
                    skippedCount: 0,
                  };

            showBulkTrackResultToast({
              result,
              selectedCount: requests.length,
              missingBodyCount,
              t,
            });

            return result.trackedCount > 0;
          } catch (error) {
            console.error('Failed to bulk track requests:', error);
            showBulkTrackResultToast({
              result: {
                trackedCount: 0,
                failedCount: requests.length,
                skippedCount: 0,
              },
              selectedCount: requests.length,
              missingBodyCount: 0,
              t,
            });
            return false;
          } finally {
            setIsPreparingBulkTrack(false);
          }
        },
        { closeDialogOnStart: true },
      );
    },
    [
      fetchBodies,
      requestManualTrack,
      resolveRequestBodyForTransport,
      t,
      trackRequests,
    ],
  );

  return {
    isBulkTracking:
      isPreparingBulkTrack || isTracking || isAwaitingManualTrackCredentials,
    trackBulkRequests,
  };
}
